import { useState } from "react";
import { LayoutGrid, ChevronDown, ChevronRight, CupSoda, Utensils, CornerDownRight } from "lucide-react";
import { buildCategoryTree } from "../../lib/categories";

const ProductList = ({ items, onAddProduct }) => (
  <ul className="mt-1 mb-1.5 ml-3 border-l border-white/10 pl-2 space-y-0.5">
    {items.map((p) => (
      <li key={p.product_id}>
        <button
          onClick={() => onAddProduct(p)}
          className="w-full text-left text-[11px] text-zinc-400 hover:text-white hover:bg-white/5 rounded px-1.5 py-1 truncate transition-colors"
          data-testid={`pos-tree-product-${p.product_id}`}
          title={`Adicionar ${p.name}`}
        >
          {p.name}
        </button>
      </li>
    ))}
    {items.length === 0 && <li className="text-[10px] text-zinc-600 px-1.5 py-1">Sem artigos</li>}
  </ul>
);

const CategoryRow = ({ cat, active, open, onSelect, onToggle, sub }) => {
  const Icon = sub ? CornerDownRight : cat.kind === "drink" ? CupSoda : Utensils;
  return (
    <div
      className={`rounded-md flex items-center pr-1 font-medium transition-colors ${sub ? "h-7 text-[11px]" : "h-8 text-xs"} ${
        active ? "bg-emerald-600 text-white" : sub ? "bg-[#202020] text-zinc-400 hover:bg-[#2a2a2a]" : "bg-[#262626] text-zinc-300 hover:bg-[#2f2f2f]"
      }`}
    >
      <button onClick={onSelect} className="flex-1 min-w-0 h-full flex items-center gap-1.5 px-2 text-left" data-testid={`pos-category-${cat.category_id}`}>
        <Icon className="w-3.5 h-3.5 shrink-0 opacity-80" />
        <span className="truncate">{cat.name}</span>
      </button>
      <button onClick={onToggle} className="w-6 h-6 flex items-center justify-center rounded hover:bg-white/10" aria-label={open ? "Recolher" : "Expandir"} data-testid={`pos-category-toggle-${cat.category_id}`}>
        {open ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
      </button>
    </div>
  );
};

export const POSCategoryTree = ({ categories, products, selectedCategory, onSelectCategory, onAddProduct }) => {
  const [expanded, setExpanded] = useState({});
  const toggle = (id) => setExpanded((e) => ({ ...e, [id]: !e[id] }));
  const select = (cat) => {
    onSelectCategory(cat.category_id);
    setExpanded((e) => ({ ...e, [cat.category_id]: true, ...(cat.parent_id ? { [cat.parent_id]: true } : {}) }));
  };
  const { roots, childrenOf } = buildCategoryTree(categories);
  const productsOf = (id) => products.filter((p) => p.category_id === id && p.is_available);

  return (
    <aside className="w-44 shrink-0 bg-[#1b1b1b] border-r border-white/5 flex flex-col overflow-hidden" data-testid="pos-category-tree">
      <button
        onClick={() => onSelectCategory("all")}
        className={`m-2 h-9 rounded-md text-xs font-semibold flex items-center gap-2 px-3 transition-colors ${
          selectedCategory === "all" ? "bg-emerald-600 text-white shadow" : "bg-[#262626] text-zinc-300 hover:bg-[#2f2f2f]"
        }`}
        data-testid="pos-category-all"
      >
        <LayoutGrid className="w-4 h-4" /> Todos os Artigos
      </button>
      <div className="flex-1 overflow-y-auto px-2 pb-2 space-y-1">
        {roots.map((cat) => {
          const subs = childrenOf[cat.category_id] || [];
          const open = !!expanded[cat.category_id];
          return (
            <div key={cat.category_id}>
              <CategoryRow cat={cat} active={selectedCategory === cat.category_id} open={open} onSelect={() => select(cat)} onToggle={() => toggle(cat.category_id)} />
              {open && (
                <div className="ml-2 mt-1 space-y-1" data-testid={`pos-subcategories-${cat.category_id}`}>
                  {subs.map((sub) => (
                    <div key={sub.category_id}>
                      <CategoryRow sub cat={sub} active={selectedCategory === sub.category_id} open={!!expanded[sub.category_id]} onSelect={() => select(sub)} onToggle={() => toggle(sub.category_id)} />
                      {expanded[sub.category_id] && <ProductList items={productsOf(sub.category_id)} onAddProduct={onAddProduct} />}
                    </div>
                  ))}
                  <ProductList items={productsOf(cat.category_id)} onAddProduct={onAddProduct} />
                </div>
              )}
            </div>
          );
        })}
        {categories.length === 0 && <p className="text-[11px] text-zinc-500 px-2 py-4 text-center">Sem categorias</p>}
      </div>
    </aside>
  );
};
